import React from 'react';
import { GestureResponderEvent, TouchableOpacity } from 'react-native';
import { Box, Inline } from '@styleseat/ui';
import { colors } from '@styleseat/brand';
import classNames from 'classnames';
import Image from '../../shared/Image';
import { HeaderText } from '..';
import './DatePicker.module.scss';
import { Moment } from '../../../modules/dateUtils';

const ChevronLeftIcon = require('../../../../images/icons/icon-chevron-left.svg');
const ChevronRightIcon = require('../../../../images/icons/icon-chevron-right.svg');

type DatePickerHeaderProps = {
  currentDate: Moment;
  disablePreviousMonth?: boolean;
  disableNextMonth?: boolean;
  onNextMonthClick?: () => void;
  onPreviousMonthClick?: () => void;
};

/**
 * Renders the header for the date picker, providing navigation between months and displaying the
 * current month and year.
 * @param {Object} param0 Component props
 * @param {moment} param0.currentDate The start of the month being displayed
 * @param {Boolean} [param0.disableNextMonth=false] True to disable the next month control
 * @param {Boolean} [param0.disablePreviousMonth=false] True to disable the previous month control
 * @param {Function} [param0.onNextMonthClick] Called when the next month control is clicked
 * @param {Function} [param0.onPreviousMonthClick] Called when the previous month control is clicked
 * @example
 * import DatePickerHeader from 'components/ui/date-picker/DatePickerHeader';
 *
 * const MyComponent = () => {
 *   return (
 *     <DatePickerHeader
 *       currentDate={moment().startOf('month')}
 *       disableNextMonth={false}
 *       disablePreviousMonth={true}
 *       onNextMonthClick={handleNextMonthClick}
 *       onPreviousMonthClick={handlePreviousMonthClick}
 *     />
 *   );
 * }
 */
const DatePickerHeader: React.FC<DatePickerHeaderProps> = ({
  currentDate,
  disableNextMonth,
  disablePreviousMonth,
  onNextMonthClick,
  onPreviousMonthClick,
}) => {
  const startOfMonth = currentDate.clone().startOf('month');

  const handlePreviousMonthPress = (evt: GestureResponderEvent) => {
    evt.preventDefault();

    onPreviousMonthClick?.();
  };

  const handleNextMonthPress = (evt: GestureResponderEvent) => {
    evt.preventDefault();

    onNextMonthClick?.();
  };

  return (
    <Inline justifyContent="space-between" alignItems="center">
      {/* We start with h2 because we should really only have h1 on a page.
       * We also add some wrapping containers for layout purposes. */}
      <Inline space={8}>
        <HeaderText level={2}>{startOfMonth.format('MMMM')}</HeaderText>
        <HeaderText level={3} lineHeight={25} color={colors.silver}>
          {startOfMonth.format('YYYY')}
        </HeaderText>
      </Inline>

      <Inline justifyContent="flex-end">
        {disablePreviousMonth ? (
          <Box minWidth={36} minHeight={48} />
        ) : (
          <TouchableOpacity
            styleName={classNames('datePickerPrevMonthButton')}
            onPress={handlePreviousMonthPress}
            accessibilityRole="button"
            testID="datePickerPrevMonthArrow"
          >
            <Image
              styleName="datePickerPrevMonthArrow"
              source={ChevronLeftIcon}
            />
          </TouchableOpacity>
        )}
        {disableNextMonth ? (
          <Box minWidth={36} minHeight={48} />
        ) : (
          <TouchableOpacity
            styleName={classNames('datePickerNextMonthButton')}
            onPress={handleNextMonthPress}
            accessibilityRole="button"
            testID="datePickerNextMonthArrow"
          >
            <Image
              styleName="datePickerNextMonthArrow"
              source={ChevronRightIcon}
            />
          </TouchableOpacity>
        )}
      </Inline>
    </Inline>
  );
};

DatePickerHeader.defaultProps = {
  onNextMonthClick: () => {},
  onPreviousMonthClick: () => {},
  disableNextMonth: false,
  disablePreviousMonth: false,
};

export default DatePickerHeader;
